'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { PrudsysHomepageDocument } from '@packages/gql/generated/shopping/PrudsysHomepageDocument';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type PrudsysHomepageProductsRecoSliderProps = ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysHomepage($locale: String!) {
    prudsysHomepage(locale: $locale) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
      extendedViewUrl
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysHomepage';

/**
 * PrudsysHomepageProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysHomepageProductsRecoSlider = ({
  backgroundColor,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  preferTitle = false,
  title,
}: PrudsysHomepageProductsRecoSliderProps) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysHomepageDocument,
    variables: {
      locale,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysHomepage.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysHomepage.type;

  useProductsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title: data?.prudsysHomepage.title || '',
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <div ref={recoEl}>
      <RecoCarousel
        title={preferTitle ? title : data?.prudsysHomepage.title || title}
        loadingState={!filteredRecommendations}
        backgroundColor={backgroundColor}
      >
        {filteredRecommendations?.map((recommendation, idx) => (
          <RecoProductCard
            // rule is disabled because order doesn't change between rerenders
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={idx}
            data={recommendation}
            onClick={(akl, sku) => {
              onProductClick?.(akl, sku);
            }}
            position={idx + 1}
            rdeTypeAbbr={rdeTypeAbbr}
          />
        ))}
      </RecoCarousel>
    </div>
  );
};
